import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import type { SecuritySettings } from '../../types/settings';

interface SecuritySettingsProps {
  settings: SecuritySettings;
  onSave: (data: SecuritySettings) => void;
  isLoading: boolean;
}

const SecuritySettings: React.FC<SecuritySettingsProps> = ({
  settings,
  onSave,
  isLoading
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { signOut } = useAuth();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    
    const data: SecuritySettings = {
      sessionTimeout: parseInt(formData.get('sessionTimeout') as string),
      allowMultipleLogins: formData.get('allowMultipleLogins') === 'true',
      twoFactorEnabled: formData.get('twoFactorEnabled') === 'true',
      maxLoginAttempts: parseInt(formData.get('maxLoginAttempts') as string),
      passwordPolicy: {
        minLength: parseInt(formData.get('passwordMinLength') as string),
        requireNumbers: formData.get('passwordRequireNumbers') === 'true',
        requireSpecialChars: formData.get('passwordRequireSpecialChars') === 'true',
        requireUppercase: formData.get('passwordRequireUppercase') === 'true',
        expirationDays: parseInt(formData.get('passwordExpirationDays') as string)
      }
    };

    onSave(data);
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);

      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not found');

      // Delete all user data in order
      await supabase.from('sale_items').delete().eq('sale_id', supabase.from('sales').select('id').eq('user_id', user.id));
      await supabase.from('fiscal_documents').delete().eq('user_id', user.id);
      await supabase.from('sales').delete().eq('user_id', user.id);
      await supabase.from('transactions').delete().eq('user_id', user.id);
      await supabase.from('products').delete().eq('user_id', user.id);
      await supabase.from('customers').delete().eq('user_id', user.id);
      await supabase.from('company_settings').delete().eq('user_id', user.id);
      await supabase.from('profiles').delete().eq('id', user.id);

      // Delete auth user
      const { error } = await supabase.auth.admin.deleteUser(user.id);
      if (error) throw error;

      // Sign out
      await signOut();

    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Erro ao excluir conta. Por favor, tente novamente.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Configurações de Segurança
        </h2>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Tempo de Expiração da Sessão (minutos)
            </label>
            <input
              type="number"
              name="sessionTimeout"
              defaultValue={settings.sessionTimeout}
              min="1"
              required
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Máximo de Tentativas de Login
            </label>
            <input
              type="number"
              name="maxLoginAttempts"
              defaultValue={settings.maxLoginAttempts}
              min="1"
              required
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Tamanho Mínimo da Senha
            </label>
            <input
              type="number"
              name="passwordMinLength"
              defaultValue={settings.passwordPolicy.minLength}
              min="6"
              required
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Expiração da Senha (dias)
            </label>
            <input
              type="number"
              name="passwordExpirationDays"
              defaultValue={settings.passwordPolicy.expirationDays}
              min="0"
              required
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              name="allowMultipleLogins"
              defaultChecked={settings.allowMultipleLogins}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">
              Permitir múltiplos logins simultâneos
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="twoFactorEnabled"
              defaultChecked={settings.twoFactorEnabled}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">
              Habilitar autenticação em duas etapas
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="passwordRequireNumbers"
              defaultChecked={settings.passwordPolicy.requireNumbers}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">
              Exigir números na senha
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="passwordRequireSpecialChars"
              defaultChecked={settings.passwordPolicy.requireSpecialChars}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">
              Exigir caracteres especiais na senha
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="passwordRequireUppercase"
              defaultChecked={settings.passwordPolicy.requireUppercase}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">
              Exigir letras maiúsculas na senha
            </label>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Salvando...' : 'Salvar Alterações'}
        </button>
      </div>

      <div className="mt-12 pt-8 border-t border-gray-200">
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Zona de Perigo
              </h3>
              <p className="mt-2 text-sm text-red-700">
                As ações abaixo são irreversíveis e resultarão na perda permanente de dados.
                Certifique-se de fazer backup de todas as informações importantes antes de prosseguir.
              </p>
            </div>
          </div>
        </div>

        {showDeleteConfirm ? (
          <div className="bg-white p-6 border border-red-200 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Confirmar Exclusão da Conta
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Tem certeza que deseja excluir sua conta? Essa ação é irreversível e todos os seus dados serão permanentemente removidos.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? 'Excluindo...' : 'Confirmar Exclusão'}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Excluir Minha Conta
          </button>
        )}
      </div>
    </form>
  );
};

export default SecuritySettings;